import React from 'react';

// Hooks
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Utils
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';

import { Box, Stepper, Step, StepLabel, Typography, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// Components
import BatchingDetailsSkeleton from './BatchingDetailsSkeleton';

// Styles
import './styles.scss';

const getStatusClassName = (status) => {
  switch (status) {
    case 'Processing':
    case 'Completed':
    case 'Mailed':
      return 'inProcess';
    case 'Scheduled':
      return 'scheduled';
    case 'Canceled':
    case 'Failed':
      return 'canceled';
    default:
      return 'statusChip';
  }
};

const BatchingDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const batchingData = useSelector((state) => state.ordersReducers.OrderDetailsBatching);
  // Product info comes from the parent order (all orders in a batch share the same product)
  const orderProduct = useSelector(
    (state) => state.ordersReducers.detailOverview?.stepper?.product
  );

  const url = window.location.pathname;

  const handleClick = (rowId) => {
    navigate(`${url.substring(0, url.lastIndexOf('/') + 1)}${rowId}`);
  };

  const columns = [
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader',
      cellClassName: 'dataGridCell',
      renderCell: (params) => <Typography>{params.row.orderNumber}</Typography>,
    },
    {
      field: 'scheduledDate',
      headerName: 'Mail Date',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader',
      cellClassName: 'dataGridCell',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader statusColumnHeader',
      cellClassName: 'dataGridCell statusColumnCell',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          className={`statusChip ${getStatusClassName(params.value)}`}
        />
      ),
    },
  ];

  // Response shape may vary. Support either a flat array, `{ orders: [...] }`,
  // or `{ batching, orders: [...] }` without breaking the tab.
  const batchingOrders = Array.isArray(batchingData)
    ? batchingData
    : batchingData?.orders ?? [];

  // Build the product label for stepper steps.
  // Postcards include paper size (e.g. "Postcards 4x6") because multiple sizes exist.
  // All other product types use the product name only (e.g. "Professional Letters").
  const buildProductLabel = () => {
    if (!orderProduct?.productType) return null;
    const type = orderProduct.productType;
    const isPostcard = type === 'Postcards';
    return isPostcard && orderProduct.paperSize
      ? `${type} ${orderProduct.paperSize}`
      : type;
  };

  const productLabel = buildProductLabel();

  const rows = batchingOrders?.length
    ? batchingOrders.map((order, idx) => ({
      id: idx,
      orderNumber: order.id,
      scheduledDate: dateFormat(order.deliveryDate, true, true),
      status: order.status,
    }))
    : [];

  const steps = batchingOrders?.length
    ? batchingOrders.map((order, idx) => {
      const batchNumber = idx + 1;
      // Prefer per-order product data; fall back to parent order's product (same product for all batches)
      const perOrderType = order.product?.productType;
      const perOrderLabel = perOrderType
        ? perOrderType === 'Postcards' && order.product.paperSize
          ? `${perOrderType} ${order.product.paperSize}`
          : perOrderType
        : null;
      const baseLabel = perOrderLabel || productLabel;
      const label = baseLabel
        ? `${baseLabel}, Batch ${batchNumber}`
        : `Batch ${batchNumber}`;

      return {
        id: idx,
        label,
        subtitle: `Order #${order.id}`,
        status: order.status,
        orderId: order.id,
      };
    })
    : [];

  // Only highlight the current order's step — "completed" = you are viewing this order.
  // We do not spread MUI's props to prevent it injecting its own completed/active flags.
  const CustomStepIcon = ({ completed, icon }) => (
    <div className={`stepIcon ${completed ? 'completed' : ''}`}>
      <span className="stepNumber">{icon}</span>
    </div>
  );

  // null means data hasn't arrived from the API yet — show skeleton (same pattern as CampaignDetails)
  if (batchingData === null) {
    return <BatchingDetailsSkeleton />;
  }

  if (!batchingOrders?.length) {
    return (
      <Box className="noCampaignsContainer">
        <Typography variant="h6" className="sectionTitle">
          {MESSAGES.ORDERS.DETAIL.BATCHING.NO_BATCHES}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="campaignDetailsContainer">
      <Box className="campaignContent">
        {/* Left side - Stepper */}
        <Box className="stepperContainer">
          <Typography variant="h6" className="sectionTitle">
            Batch Progress
          </Typography>

          <Stepper orientation="vertical" className="customStepper" activeStep={-1}>
            {steps.map((step, index) => (
              <Step key={step.id} className="customStep">
                <StepLabel
                  StepIconComponent={() => (
                    <CustomStepIcon
                      completed={String(step.orderId) === String(id)}
                      icon={index + 1}
                    />
                  )}
                  className="customStepLabel"
                >
                  <Box className="stepLabelContent">
                    <Typography className="stepTitle">{step.label}</Typography>
                    <Typography className="stepSubtitle">{step.subtitle}</Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Right side - DataGrid Table */}
        <Box className="tableContainerCampaignOrder">
          <DataGrid
            rows={rows}
            columns={columns}
            hideFooter
            rowHeight={56}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            disableColumnSorting
            onRowClick={(params) => handleClick(params.row.orderNumber)}
            getRowClassName={(params) =>
              String(params.row.orderNumber) === String(id) ? 'rowWithId' : ''
            }
            classes={{
              virtualScroller: `virtualScroller ${
                rows.length > 4 ? 'normalVirtualScroller' : 'minHeightVirtualScroller'
              }`,
            }}
            className="customDataGrid"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BatchingDetails;
